module Client.Admin.ResetPassword.Grid

open Shared
open Client
open Client.Admin
open Elmish
open Illy.SafeStack.Client 
open Illy.SafeStack.Client.jQueryUI
open Fable.React
open Fable.React.Props
open Shared.Admin
open SafeStack.Client

type Model =
    {
        Grid: UserGrid.Model
        Edit: Edit.Model option
        ResetMFA: ResetMFA.Model option
        MFAEnabled: bool
    }

type Msg =
    | UserGridMsg of UserGrid.Msg
    | EditMsg of Edit.Msg
    | ResetMFAMsg of ResetMFA.Msg
    | ResetPassword of UserId
    | ClearMFASeed of UserId
    | NotificationWasHidden
    | MFAEnabledLoaded of bool
    | ApiError of ApiError

type Event =
    | ApiError of ApiError
    | CloseOverlay

module Call =
    let mfaEnabled () =
        Api.secureCall SecuredRoute.Admin (fun (x: IResetPasswordFromAdminApi) -> x.mfaEnabled) () MFAEnabledLoaded Msg.ApiError

let init () =
    let m, c = UserGrid.init {UserGrid.IncludeUsersFilter.all with Locked = false; Disabled = false}
    {
        Grid = m
        Edit = None
        ResetMFA = None
        MFAEnabled = false
    }, Cmd.batch [
        Call.mfaEnabled ()
        Cmd.map UserGridMsg c
    ]

let dispatchEditMsg model msg =
    match model.Edit with 
    | None -> model, SideEffect.none
    | Some edit ->
        let m, eff = Edit.update msg edit
        match eff with
        | SideEffect.Cmd c -> {model with Edit = Some m}, Cmd.map EditMsg c |> SideEffect.Cmd
        | SideEffect.Event ev ->
            match ev with
            | Edit.Event.ApiError err ->
                {model with Edit = Some m}, err |> ApiError |> SideEffect.Event
            | Edit.Event.CloseOverlay ->
                {model with Edit = None}, SideEffect.none
            | Edit.Event.SaveSuccessful notification ->
                {model with Edit = None},
                Cmd.batch [
                    Notification.success notification NotificationWasHidden
                    Cmd.ofMsg (UserGrid.Msg.RefreshGrid |> UserGridMsg)
                ] |> SideEffect.Cmd

let dispatchResetMFAMsg model msg =
    match model.ResetMFA with 
    | None -> model, SideEffect.none
    | Some edit ->
        let m, eff = ResetMFA.update msg edit
        match eff with
        | SideEffect.Cmd c -> {model with ResetMFA = Some m}, Cmd.map ResetMFAMsg c |> SideEffect.Cmd
        | SideEffect.Event ev ->
            match ev with
            | ResetMFA.Event.ApiError err ->
                {model with ResetMFA = Some m}, err |> ApiError |> SideEffect.Event
            | ResetMFA.Event.CloseOverlay ->
                {model with ResetMFA = None}, SideEffect.none
            | ResetMFA.Event.SaveSuccessful notification ->
                {model with ResetMFA = None},
                Cmd.batch [
                    Notification.success notification NotificationWasHidden
                    Cmd.ofMsg (UserGrid.Msg.RefreshGrid |> UserGridMsg)
                ] |> SideEffect.Cmd

let update msg (model: Model) =
    match msg with
    | MFAEnabledLoaded enabled -> {model with MFAEnabled = enabled}, SideEffect.none
    | UserGridMsg uMsg ->
        let m, eff = UserGrid.update uMsg model.Grid
        {model with Grid = m},
        match eff with 
        | SideEffect.Cmd c -> Cmd.map UserGridMsg c |> SideEffect.Cmd
        | SideEffect.Event ev ->
            match ev with
            | UserGrid.Event.ApiError err -> err |> ApiError |> SideEffect.Event
            | UserGrid.Event.CloseOverlay -> CloseOverlay |> SideEffect.Event
    | ResetPassword uId ->
        let m, c = Edit.init uId
        {model with Edit = Some m}, Cmd.map EditMsg c |> SideEffect.Cmd
    | ClearMFASeed uId ->
        let m, c = ResetMFA.init uId
        {model with ResetMFA = Some m}, Cmd.map ResetMFAMsg c |> SideEffect.Cmd
    | EditMsg eMsg -> dispatchEditMsg model eMsg
    | ResetMFAMsg eMsg -> dispatchResetMFAMsg model eMsg
    | NotificationWasHidden -> model, SideEffect.none
    | Msg.ApiError err -> model, err |> Event.ApiError |> SideEffect.Event

let view (model: Model) dispatch =
    [
        if model.Edit.IsNone then 
            yield! UserGrid.viewModal "Reset user passwords" model.Grid (UserGridMsg >> dispatch) [
                UserGrid.GridColumn.name (UserGrid.isLoadingMoreResults model.Grid) model.Grid.SortInfo (UserGridMsg >> dispatch)
                UserGrid.GridColumn.username (UserGrid.isLoadingMoreResults model.Grid) model.Grid.SortInfo (UserGridMsg >> dispatch)
                UserGrid.GridColumn.email (UserGrid.isLoadingMoreResults model.Grid) model.Grid.SortInfo (UserGridMsg >> dispatch)
                Some [ str "Actions" ] ===> fun u -> [
                    yield span [ Class "emblems-container emblems-spaced" ] [
                        yield UI.button "Reset password" (ResetPassword (UserId u.Id)) dispatch
                        if model.MFAEnabled then
                            yield UI.button "Reset MFA" (ClearMFASeed (UserId u.Id)) dispatch
                    ]
                ]
            ]
        yield! jQueryUI.conditionallyRenderView model.Edit Edit.view EditMsg dispatch
        yield! jQueryUI.conditionallyRenderView model.ResetMFA ResetMFA.view ResetMFAMsg dispatch
    ]